import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { usePageTitle } from '@confluence/content-title/entry-points/usePageTitle.ts';
import { withSmartCardProviderContext } from '@confluence/editor-features/entry-points/withSmartCardProviderContext.ts';
import { type DevDocsBylineConnectionAncestor as DevDocsBylineConnectionAncestorType } from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { LazyGlobalFeedbackCollector } from '@confluence/global-feedback-collector/entry-points/GlobalFeedbackCollector.ts';
import { FeedbackSource } from '@confluence/global-feedback-collector/entry-points/feedbackSourceType.ts';
import { RelaySuspense } from '@confluence/query-preloader-tools/entry-points/RelaySuspense.ts';

import { useIsTeamworkGraphPreviewPanelOpen } from './teamwork-graph-preview-panel/teamworkGraphPreviewPanelState.ts';
import type { AtAGlanceObservation } from './at-a-glance/atAGlanceObservation.ts';
import { useAtAGlanceObservation } from './at-a-glance/useAtAGlanceObservation.ts';
import { BylineConnectionsPopup } from './BylineConnectionsPopup.tsx';
import { useConnectionsFeedback } from './feedback/useConnectionsFeedback.ts';
import { ConnectionsFeedbackFlag } from './feedback/ConnectionsFeedbackFlag.tsx';
import { BylineConnectionsPlaceholder } from './ssr/BylineConnectionsPlaceholder.tsx';
import { useBylineConnectionsAnalytics } from './hooks/useBylineConnectionsAnalytics.ts';
import { useBylineConnectionsData } from './hooks/useBylineConnectionsData.ts';
import { useBylineConnectionsInteraction } from './hooks/useBylineConnectionsInteraction.ts';
import { useBylineConnectionsVisibility } from './hooks/useBylineConnectionsVisibility.ts';
import {
	useBylineTileReservation,
	type BylineTileReservation,
} from './hooks/useBylineTileReservation.ts';
import { useBylineSsrSampleAnalytics } from './hooks/useBylineSsrSampleAnalytics.ts';
import { useTeamworkGraphPreviewPanel } from './teamwork-graph-preview-panel/useTeamworkGraphPreviewPanel.tsx';
import type { BylineContentType as BylineContentTypeType } from './utils/contentAri.ts';

export type BylineConnectionsProps = {
	contentId: string;
	/**
	 * Pages and blog posts are distinct Teamwork Graph node types, so every graph query and the
	 * At-a-glance request are keyed on this.
	 */
	contentType: BylineContentType;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
	pageVersion?: number | null;
	pageUpdatedAt?: string | null;
	/** Optional override of the default Context Hub feedback collector. */
	onShareFeedback?: () => void;
};

export type DevDocsBylineConnectionAncestor = DevDocsBylineConnectionAncestorType;
export type BylineContentType = BylineContentTypeType;

// Keep the invitation hidden until the product launch is ready.
const SHOW_CONNECTIONS_FEEDBACK_INVITATION = false;

const BylineConnectionsInner: FC<
	BylineConnectionsProps & {
		observation: AtAGlanceObservation;
		reservation: BylineTileReservation;
		openFeedback: () => void;
	}
> = (props) => {
	const { contentId, contentType, openFeedback } = props;
	const { title, error: pageTitleError } = usePageTitle({ contentId });
	const { openTeamworkGraphPreviewPanel } = useTeamworkGraphPreviewPanel(contentId, contentType);
	const [atAGlanceEnabled, setAtAGlanceEnabled] = useState(false);
	const {
		isOpen,
		triggerRef,
		handleClose: closeMenu,
		handleNavigate,
		handleOpenPreviewPanel,
		handleToggle: toggleMenu,
		handlePlaceholderOpenChange,
	} = useBylineConnectionsInteraction({
		contentId,
		openTeamworkGraphPreviewPanel,
	});
	// Capture opening before request effects, then resolve after them to identify newly started work.
	useEffect(() => {
		props.observation.observePopup(isOpen);
	}, [isOpen, props.observation]);
	const data = useBylineConnectionsData({
		...props,
		pageTitle: pageTitleError ? null : title,
		atAGlanceEnabled,
		observation: props.observation,
	});
	const { outgoing, incoming, relatedContent, devDocsConnection } = data;
	// Embedded Confluence has no preview panel. Keep experiment evaluation short-circuited.
	const isEmbeddedMenu =
		useIsEmbeddedConfluence_DO_NOT_USE() &&
		isExperimentEnabled('confluence_1p_and_3p_connection_byline_experiment');
	const handleClose = useCallback(() => closeMenu(isEmbeddedMenu), [closeMenu, isEmbeddedMenu]);
	const visibility = useBylineConnectionsVisibility({
		contentId,
		contentType,
		isEmbeddedMenu,
		reservation: props.reservation,
		data,
	});
	const isMenuReady =
		isOpen &&
		!isEmbeddedMenu &&
		!visibility.hiddenBySsr &&
		!visibility.isResolving &&
		visibility.shouldRenderByline &&
		incoming.settled &&
		!incoming.loading &&
		!outgoing.loading &&
		!outgoing.refreshing &&
		!relatedContent.loading;
	// Retain activation before committing the ready menu, avoiding an effect-driven update.
	// This component is keyed by contentId, so each page visit requires another ready menu.
	if (isMenuReady && !atAGlanceEnabled) {
		setAtAGlanceEnabled(true);
	}
	useEffect(() => {
		props.observation.resolvePopupDecision();
	});
	const reportMenuOpened = useBylineConnectionsAnalytics({ contentId, visibility, data });
	useBylineSsrSampleAnalytics(visibility.isSsrLoad);
	const handleToggle = useCallback(
		() => toggleMenu(reportMenuOpened),
		[toggleMenu, reportMenuOpened],
	);
	const handleShareFeedback = useCallback(() => {
		// Hand focus to the modal instead of treating this transition as a popup dismissal.
		handleNavigate();
		openFeedback();
	}, [handleNavigate, openFeedback]);

	const { links: outgoingLinks, refreshLinkUrl } = outgoing;
	const {
		links: incomingLinks,
		twgLinks: incomingTwgLinks,
		hasNextPage: incomingLinksHaveMore,
		settled: incomingLinksSettled,
	} = incoming;
	const { links: relatedContentLinks, loading: relatedContentLoading } = relatedContent;
	// The loaded stack keeps the tiles the placeholder painted. CCINT-9543
	const { devDocsIcons, paintedIconCount } = visibility;
	// One set of icon-stack inputs for the Context trigger and the menu's "View more context" chips,
	// so they always show the same icons. CCINT-9543
	const iconStackProps = useMemo(
		() => ({
			links: outgoingLinks,
			incomingLinks: incomingTwgLinks,
			relatedContentLinks,
			additionalIcons: devDocsIcons,
			// Same width as the placeholder: no more icons than it painted, and its grey link tiles in
			// any slot the real icons leave empty. With no placeholder, just the real icons.
			minIcons: paintedIconCount,
			maxIcons: paintedIconCount,
		}),
		[outgoingLinks, incomingTwgLinks, relatedContentLinks, devDocsIcons, paintedIconCount],
	);
	const menuProps = useMemo(
		() => ({
			isEmbeddedMenu,
			links: outgoingLinks,
			incomingLinks,
			incomingLinksHaveMore,
			incomingLinksSettled,
			additionalIcons: devDocsIcons,
			relatedContentLinks,
			relatedContentLoading,
			devDocsConnection,
			refreshLinkUrl,
			onNavigate: handleNavigate,
			onOpenPreviewPanel: handleOpenPreviewPanel,
			viewMoreContextIcons: iconStackProps,
		}),
		[
			isEmbeddedMenu,
			outgoingLinks,
			incomingLinks,
			incomingLinksHaveMore,
			incomingLinksSettled,
			devDocsIcons,
			relatedContentLinks,
			relatedContentLoading,
			devDocsConnection,
			refreshLinkUrl,
			handleNavigate,
			handleOpenPreviewPanel,
			iconStackProps,
		],
	);
	const triggerProps = useMemo(
		() => ({ ...iconStackProps, triggerRef, onToggle: handleToggle }),
		[iconStackProps, triggerRef, handleToggle],
	);

	if (visibility.hiddenBySsr) {
		return null;
	}
	// Loaded visibility is provisional while queries resolve; the placeholder paints the reserved
	// tiles, which is nothing after a client-side navigation.
	if (visibility.isResolving) {
		return (
			<BylineConnectionsPlaceholder
				iconCount={visibility.reservedIconCount}
				isOpen={isOpen}
				onOpenChange={handlePlaceholderOpenChange}
			/>
		);
	}
	if (!visibility.shouldRenderByline) {
		return null;
	}

	return (
		<>
			{SHOW_CONNECTIONS_FEEDBACK_INVITATION && !isEmbeddedMenu && (
				<ConnectionsFeedbackFlag
					contentId={contentId}
					isMenuReady={isMenuReady}
					onShareFeedback={handleShareFeedback}
				/>
			)}
			<BylineConnectionsPopup
				isOpen={isOpen}
				onClose={handleClose}
				menuProps={menuProps}
				triggerProps={triggerProps}
			/>
		</>
	);
};

const BylineConnectionsWithSuspense: FC<BylineConnectionsProps> = (props) => {
	const { isFeedbackOpen, openFeedback, closeFeedback } = useConnectionsFeedback({
		contentId: props.contentId,
		onShareFeedback: props.onShareFeedback,
	});
	const observation = useAtAGlanceObservation(props.contentId);
	// The inner component cannot commit panel transitions while its data queries suspend.
	const panelOpen = useIsTeamworkGraphPreviewPanelOpen(props.contentId);
	useEffect(() => {
		observation.observePanel(panelOpen);
	}, [observation, panelOpen]);
	// https://hello.atlassian.net/browse/CCINT-9575: the suspended popup owns its state, so observe its commit rather than a click.
	const observeFallbackPopup = useCallback(
		(open: boolean) => {
			observation.observePopup(open);
			observation.resolvePopupDecision();
		},
		[observation],
	);
	return (
		<TransparentErrorBoundary key={props.contentId} attribution={Attribution.INTEGRATIONS}>
			<BylineConnectionsWithReservation
				{...props}
				observation={observation}
				onOpenObserved={observeFallbackPopup}
				openFeedback={openFeedback}
			/>
			{isFeedbackOpen && (
				<LazyGlobalFeedbackCollector
					feedbackSource={FeedbackSource.CONTEXT_HUB}
					contentType={props.contentType}
					onClose={closeFeedback}
					onCloseComplete={() => {}}
				/>
			)}
		</TransparentErrorBoundary>
	);
};

// Inside the error boundary, so a failed sample read stays contained, and above the Suspense
// boundary, so the count is captured on the first render even when the loaded byline suspends and
// mounts later. Both placeholders and the loaded byline read this one value. CCINT-9543
const BylineConnectionsWithReservation: FC<
	BylineConnectionsProps & {
		observation: AtAGlanceObservation;
		onOpenObserved: (open: boolean) => void;
		openFeedback: () => void;
	}
> = ({ onOpenObserved, ...props }) => {
	const reservation = useBylineTileReservation({
		contentId: props.contentId,
		contentType: props.contentType,
		connectionAncestors: props.connectionAncestors,
	});
	return (
		<RelaySuspense
			interactionName="BylinePageAdfQuery"
			fallback={
				<BylineConnectionsPlaceholder
					iconCount={reservation.reservedIconCount}
					onOpenObserved={onOpenObserved}
				/>
			}
		>
			<BylineConnectionsInner {...props} reservation={reservation} />
		</RelaySuspense>
	);
};

export const BylineConnections: (props: any) => React.JSX.Element = withSmartCardProviderContext(
	BylineConnectionsWithSuspense,
);
